/**
 * High-level notes hook combining store and sync
 * Provides complete note management with automatic sync
 */

import { useCallback, useMemo } from 'react';
import { StoredNote, NotePayload, NoteCategory } from '@maco144/shared';
import { useNoteStore } from './use-note-store';
import { useNoteSync, type NoteSyncOptions } from './use-note-sync';

export interface UseNotesOptions extends NoteSyncOptions {
  wsUrl?: string;
}

/**
 * Combined hook for managing notes with automatic sync
 * Handles both offline caching and server synchronization
 */
export function useNotes(userId?: string, options: UseNotesOptions = {}) {
  // Initialize store
  const store = useNoteStore(userId);

  // Initialize sync
  const sync = useNoteSync(store, userId, options.wsUrl, options);

  // Create note
  const createNote = useCallback(
    async (payload: NotePayload): Promise<StoredNote | null> => {
      if (!store.isInitialized || !userId) return null;

      try {
        // Generate note ID
        const noteId = `note:${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Store locally
        await store.setNote(noteId, payload, userId);

        // Add to pending sync
        sync.addPendingChange(noteId, 'create', payload);

        // Return the stored note
        const note = await store.getNote(noteId);
        return note || null;
      } catch (err) {
        console.error('Failed to create note:', err);
        return null;
      }
    },
    [store, userId, sync]
  );

  // Update note
  const updateNote = useCallback(
    async (noteId: string, updates: Partial<NotePayload>): Promise<StoredNote | null> => {
      if (!store.isInitialized || !userId) return null;

      try {
        const existing = await store.getNote(noteId);
        if (!existing) return null;

        // Merge updates
        const updated: NotePayload = {
          ...existing,
          ...updates,
          updatedAt: Date.now(),
        };

        // Store locally
        await store.setNote(noteId, updated, userId);

        // Add to pending sync
        sync.addPendingChange(noteId, 'update', updated);

        return await store.getNote(noteId);
      } catch (err) {
        console.error('Failed to update note:', err);
        return null;
      }
    },
    [store, userId, sync]
  );

  // Delete note
  const deleteNote = useCallback(
    async (noteId: string): Promise<void> => {
      if (!store.isInitialized || !userId) return;

      try {
        // Delete from local store
        await store.deleteNote(noteId);

        // Add to pending sync
        sync.addPendingChange(noteId, 'delete');
      } catch (err) {
        console.error('Failed to delete note:', err);
      }
    },
    [store, userId, sync]
  );

  // Get single note
  const getNote = useCallback(
    async (noteId: string): Promise<StoredNote | undefined> => {
      if (!store.isInitialized) return undefined;
      return store.getNote(noteId);
    },
    [store]
  );

  // Get all notes
  const getAllNotes = useCallback(async (): Promise<StoredNote[]> => {
    if (!store.isInitialized) return [];
    return store.getAllNotes();
  }, [store]);

  // Get notes by category
  const getNotesByCategory = useCallback(
    async (category: NoteCategory): Promise<StoredNote[]> => {
      if (!store.isInitialized) return [];
      return store.getNotesByCategory(category);
    },
    [store]
  );

  // Get notes by tag
  const getNotesByTag = useCallback(
    async (tag: string): Promise<StoredNote[]> => {
      if (!store.isInitialized) return [];
      return store.getNotesByTag(tag);
    },
    [store]
  );

  // Get planted notes
  const getPlantedNotes = useCallback(async (): Promise<StoredNote[]> => {
    if (!store.isInitialized) return [];
    return store.getPlantedNotes();
  }, [store]);

  // Get archived notes
  const getArchivedNotes = useCallback(async (): Promise<StoredNote[]> => {
    if (!store.isInitialized) return [];
    return store.getArchivedNotes();
  }, [store]);

  // Plant note to floor
  const plantNote = useCallback(
    async (
      noteId: string,
      floorPosition: { x: number; y: number; z: number },
      visualType: 'sticky' | 'notebook' | 'card' = 'sticky'
    ): Promise<StoredNote | null> => {
      if (!store.isInitialized || !userId) return null;

      try {
        const existing = await store.getNote(noteId);
        if (!existing) return null;

        // Update note
        const updated: NotePayload = {
          ...existing,
          isPlanted: true,
          floorPosition,
          visualType,
          updatedAt: Date.now(),
        };

        await store.setNote(noteId, updated, userId);

        // Add to pending sync
        sync.addPendingChange(noteId, 'plant', updated);

        return await store.getNote(noteId);
      } catch (err) {
        console.error('Failed to plant note:', err);
        return null;
      }
    },
    [store, userId, sync]
  );

  // Unplant note from floor
  const unplantNote = useCallback(
    async (noteId: string): Promise<StoredNote | null> => {
      if (!store.isInitialized || !userId) return null;

      try {
        const existing = await store.getNote(noteId);
        if (!existing) return null;

        // Update note
        const updated: NotePayload = {
          ...existing,
          isPlanted: false,
          updatedAt: Date.now(),
        };

        await store.setNote(noteId, updated, userId);

        // Add to pending sync
        sync.addPendingChange(noteId, 'unplant', updated);

        return await store.getNote(noteId);
      } catch (err) {
        console.error('Failed to unplant note:', err);
        return null;
      }
    },
    [store, userId, sync]
  );

  // Archive note
  const archiveNote = useCallback(
    async (noteId: string): Promise<StoredNote | null> => {
      return updateNote(noteId, { archived: true });
    },
    [updateNote]
  );

  // Unarchive note
  const unarchiveNote = useCallback(
    async (noteId: string): Promise<StoredNote | null> => {
      return updateNote(noteId, { archived: false });
    },
    [updateNote]
  );

  // Pin note
  const pinNote = useCallback(
    async (noteId: string): Promise<StoredNote | null> => {
      return updateNote(noteId, { pinned: true });
    },
    [updateNote]
  );

  // Unpin note
  const unpinNote = useCallback(
    async (noteId: string): Promise<StoredNote | null> => {
      return updateNote(noteId, { pinned: false });
    },
    [updateNote]
  );

  // Export all notes
  const exportNotes = useCallback(async (): Promise<Record<string, NotePayload>> => {
    if (!store.isInitialized) return {};
    return store.exportAll();
  }, [store]);

  // Import notes
  const importNotes = useCallback(
    async (data: Record<string, NotePayload>): Promise<void> => {
      if (!store.isInitialized) return;
      return store.importAll(data);
    },
    [store]
  );

  // Manual sync
  const manualSync = useCallback(async (): Promise<void> => {
    await sync.syncFromServer();
    await sync.syncToServer();
  }, [sync]);

  return {
    // State
    isInitialized: store.isInitialized,
    isLoading: store.isLoading,
    isSyncing: sync.isSyncing,
    lastSyncTime: sync.lastSyncTime,
    syncError: sync.syncError,
    pendingChanges: sync.pendingChanges,
    failedChanges: sync.failedChanges,

    // CRUD operations
    createNote,
    updateNote,
    deleteNote,
    getNote,
    getAllNotes,

    // Search & filtering
    getNotesByCategory,
    getNotesByTag,
    getPlantedNotes,
    getArchivedNotes,

    // Plant/unplant
    plantNote,
    unplantNote,

    // Archive/pin
    archiveNote,
    unarchiveNote,
    pinNote,
    unpinNote,

    // Bulk operations
    exportNotes,
    importNotes,

    // Sync
    manualSync,
  };
}
